import { localeSelected } from '@libs/src/locale/locale.helper';
import { AccountSegmentSelection } from '@libs/src/models/account.model';
import { Group, GroupCustomSegment } from '@libs/src/models/group.model';
import { getSegmentationSelection } from '@libs/src/segment-selections/segment-selections.helper';
import { baseProfileCsvHeaderMap } from '@main-client/src/app/download-csv-template/download-csv-template.helper';
import { AccountUpdateError } from '@main-client/src/app/group-edit-members/group-edit-members';

import find from 'lodash-es/find';
import get from 'lodash-es/get';
import includes from 'lodash-es/includes';
import isArray from 'lodash-es/isArray';
import isBoolean from 'lodash-es/isBoolean';
import isEmpty from 'lodash-es/isEmpty';
import isFunction from 'lodash-es/isFunction';
import isUndefined from 'lodash-es/isUndefined';
import pick from 'lodash-es/pick';
import reduce from 'lodash-es/reduce';
import set from 'lodash-es/set';
import some from 'lodash-es/some';

const baseFieldErrorMessagesMap = {
  'groups.industries': {
    enum: (group: Group, value: string[]) =>
      `"interests" options do not exist in "${group.name}": ${value.join(
        ', ',
      )}`,
    type: () => '"interests" must be a list separated by new lines"',
  },
  'groups.inviteable': {
    type: () => '"inviteable" must be "TRUE" or "FALSE"',
  },
  'groups.opted_into_matchmaking': {
    type: () => '"introductions" must be "TRUE" or "FALSE"',
  },
  'groups.searchable': {
    type: () => '"searchable" must be "TRUE" or "FALSE"',
  },
  'locale_config.locale_selected': {
    enum: () => `"locale" can only be: "${localeSelected.join('", "')}"`,
  },
};

const fieldsToConvertToBoolean = [
  'groups.inviteable',
  'groups.opted_into_matchmaking',
  'groups.searchable',
];
const fieldsToConvertToArray = [
  'groups.industries',
  'groups.offerings',
  'groups.seeking',
];

export function buildUpdateBody(item: any, group: Group, csvHeaders: string[]) {
  const itemBaseFields = pick(item, csvHeaders);
  const mappedItemBasedFields: any = reduce(
    itemBaseFields,
    (result, value, key) => {
      const mappedField = get(baseProfileCsvHeaderMap, key);
      if (mappedField && !isUndefined(value)) {
        const valueToSet = convertValueToExpectedType(value, mappedField);
        return set(result, mappedField, valueToSet);
      }
      return result;
    },
    {},
  );
  const customSegmentsCsvHeaderMap = getCustomSegmentTitles(
    group.custom_segments,
  ).map((title: string) => `"${title}"`);
  const itemsCustomSegmentFields = pick(item, customSegmentsCsvHeaderMap);
  const segmentSelections = reduce(
    itemsCustomSegmentFields,
    (result, value, key) => {
      const customSegment = find(
        group.custom_segments,
        (groupCustomSegment: GroupCustomSegment) =>
          key === `"${groupCustomSegment.title}"`,
      );
      if (customSegment && !isUndefined(value)) {
        result.push({
          custom_segment: customSegment._id,
          ...('multi' === customSegment.selection_type
            ? {
                multiselected_nodes: isArray(value)
                  ? value
                  : (value && value.split('\n')) || [],
              }
            : { node: value }),
        });
        return result;
      }
      return result;
    },
    [],
  );
  return {
    ...mappedItemBasedFields,
    ...(isEmpty(itemsCustomSegmentFields)
      ? {}
      : {
          groups: {
            ...mappedItemBasedFields.groups,
            segment_selections: segmentSelections,
          },
        }),
  };
}

export function convertToArray(value: string = '') {
  return value
    ? value
        .trim()
        .split(/\n/)
        .map((convertedValue: string) => convertedValue.trim())
    : [];
}

export function convertToBoolean(value: string = '') {
  if ('false' === value.toLowerCase()) {
    return false;
  }
  if ('true' === value.toLowerCase()) {
    return true;
  }
  return value;
}

export function convertValueToExpectedType(value: string, field: string) {
  if (!isBoolean(value) && includes(fieldsToConvertToBoolean, field)) {
    return convertToBoolean(value);
  }
  if (!isArray(value) && includes(fieldsToConvertToArray, field)) {
    return convertToArray(value);
  }
  return value;
}

export function getCustomSegmentTitles(
  groupCustomSegments: GroupCustomSegment[] = [],
) {
  return groupCustomSegments.map(
    (customSegment: GroupCustomSegment) => customSegment.title,
  );
}

export function buildErrorMessage(
  errors: AccountUpdateError[] = [],
  group: Group,
) {
  return errors
    .map((error: AccountUpdateError) => {
      const isBaseFieldError = !includes(
        error.path,
        'groups.segment_selections',
      );
      if (isBaseFieldError) {
        const errorMessageBuilder = get(baseFieldErrorMessagesMap, [
          error.path,
          error.kind,
        ]);
        return isFunction(errorMessageBuilder)
          ? errorMessageBuilder(group, error.value)
          : error.message;
      }
      return customSegmentErrorMessagesMap(error, group);
    })
    .join('\n');
}

export function customSegmentErrorMessagesMap(
  error: AccountUpdateError,
  group: Group,
) {
  if (error.value?.multiselected_nodes) {
    const isCustomSegmentMultiNodeError = includes(
      error.path,
      'multiselected_nodes',
    );
    return customSegmentErrorMultiSelectMessages(
      error,
      group,
      isCustomSegmentMultiNodeError,
    );
  }
  const isCustomSegmentSingleNodeError = includes(error.path, 'node');
  return customSegmentErrorSingleSelectMessages(
    error,
    group,
    isCustomSegmentSingleNodeError,
  );
}

export function customSegmentErrorMultiSelectMessages(
  error: AccountUpdateError,
  group: Group,
  isCustomSegmentMultiNodeError: boolean,
) {
  const customSegment = find(
    group.custom_segments,
    (segment: GroupCustomSegment) =>
      some(error.value?.multiselected_nodes, (errNodeValue) =>
        segment.nodes.includes(errNodeValue),
      ) || error.value?.custom_segment === segment._id,
  ) as GroupCustomSegment;
  if ('enum' === error.kind) {
    return isCustomSegmentMultiNodeError
      ? `"${customSegment?.title}" value does not exist in "${group.name}": ${error.value.multiselected_nodes}`
      : `Segment id does not exist in "${group.name}": ${error.value.custom_segment}`;
  }
  if ('required' === error.kind) {
    return isCustomSegmentMultiNodeError
      ? `"${customSegment?.title}" value must be provided`
      : 'Segment id must be provided';
  }
}

export function customSegmentErrorSingleSelectMessages(
  error: AccountUpdateError,
  group: Group,
  isCustomSegmentSingleNodeError: boolean,
) {
  const customSegment = find(
    group.custom_segments,
    (segment: GroupCustomSegment) =>
      includes(segment.nodes, error.value?.node) ||
      error.value?.custom_segment === segment._id,
  );
  if ('enum' === error.kind) {
    return isCustomSegmentSingleNodeError
      ? `"${customSegment?.title}" value does not exist in "${group.name}": ${error.value.node}`
      : `Segment id does not exist in "${group.name}": ${error.value.custom_segment}`;
  }
  if ('required' === error.kind) {
    return isCustomSegmentSingleNodeError
      ? `"${customSegment?.title}" value must be provided`
      : 'Segment id must be provided';
  }
}

export function mergeSegmentSelections(
  group: Group,
  baseSegmentSelections: AccountSegmentSelection[],
  segmentSelectionsUpdate: AccountSegmentSelection[],
): AccountSegmentSelection[] {
  return group.custom_segments
    .map((segment) => {
      const targetSelection =
        getSegmentationSelection(segmentSelectionsUpdate, segment) ||
        getSegmentationSelection(baseSegmentSelections, segment);
      return { segment, selection: targetSelection };
    })
    .filter(({ segment, selection }) => {
      const selectionValue =
        'multi' === segment.selection_type
          ? selection.multiselected_nodes
          : selection.node;
      return !isEmpty(selectionValue);
    })
    .map(({ selection }) => selection);
}
